<template>
    <div id="print-queue-container">
        <DispenseStatusComponent
            :show-collapse-toggle="true"
            :collapsed="isDispenseStatusCollapsed"
            @toggle-collapse="toggleDispenseStatus" />
        <card :title="pageTitle">
            <b-row>
                <b-col>
                    <b-badge v-if="!storeFilter?.id" variant="warning" class="pull-right throb-badge"
                        style="margin-top:-2px;">
                        <i class="fas fa-exclamation-triangle" />
                        Please Select a Store
                    </b-badge>
                    <SearchComponent :id="storeFilter?.id" v-model="storeFilter" label="Store" search-u-r-l="/Store"
                        :params="{active: true}" prepopulate />
                </b-col>
                <b-col>
                    <SearchComponent :id="programFilter?.id" v-model="programFilter" label="Program"
                        search-u-r-l="/Program" :params="{ active: true }" clearable prepopulate />
                </b-col>
                <b-col>
                    <label for="order-date-filter">Order Date:</label>
                    <div class="d-flex" style="gap: 15px;" id="order-date-filter">
                        <el-date-picker v-model="orderDateStart" placeholder="Start Date" clearable today-button reset-button
                            v-b-tooltip.hover title="Filters by Order Date field" />
                        <el-date-picker v-model="orderDateEnd" placeholder="End Date" clearable today-button reset-button
                            v-b-tooltip.hover title="Filters by Order Date field" />
                    </div>
                    <small v-if="isInvalidDateRange" class="text-danger">
                        <i class="fa fa-exclamation-triangle"></i> End date must be after start date
                    </small>
                </b-col>
            </b-row>
            <b-row class="mt-2">
                <b-col class="d-flex flex-column">
                    <checkbox v-model="showOutOfStocks">Show Out Of Stocks</checkbox>
                    <checkbox v-model="showConsignment">Show Consignment</checkbox>
                    <checkbox v-model="showPrinted">Show Recently Printed</checkbox>
                </b-col>
                <b-col>
                    <label for="out-of-stock-input">Mark Out Of Stock:</label>
                    <b-form-input
                        id="out-of-stock-input"
                        v-model="outOfStockValue"
                        type="text"
                        placeholder="Enter Rx ID (Store-Rx-Rf) and press Enter..."
                        @keydown.enter.native="sendOutOfStock" />
                    <label for="quick-search-input" class="mt-2">
                        Filter:
                        <HelpPopup :icon-muted="true">
                            Search across multiple fields:
                            <ul>
                                <li>Patient name (first or last)</li>
                                <li>Drug name</li>
                                <li>NDC</li>
                                <li>Rx number</li>
                                <li>RxId (e.g., "1-12345-0")</li>
                            </ul>
                            <strong>Tip:</strong> Use multiple words to narrow results.<br>
                            Example: "jo do" matches "John Doe"
                        </HelpPopup>
                    </label>
                    <b-form-input
                        id="quick-search-input"
                        v-model="searchText"
                        type="search"
                        placeholder="Search NDC, drug, patient name, Rx#..."
                        debounce="300"
                    />
                </b-col>
                <b-col v-if="isStalePrintQueue">
                    <label for="days-old-input">Days Old:</label>
                    <b-form-input
                        id="days-old-input"
                        v-model="oldQueueDays"
                        type="number"
                        min="0"
                        v-b-tooltip.hover
                        title="The number of days since the item has been printed." />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-button variant="success" :disabled="!(storeFilter && storeFilter.id)"
                        @click="$bvModal.show('print-rx-rf-modal')">
                        Print by Rx/Rf...
                    </b-button>
                    <b-button variant="primary" @click="$bvModal.show('print-badge')">
                        Reprint Badge...
                    </b-button>
                    <b-button v-b-modal.print-queue-modal variant="info">Stats...</b-button>
                </b-col>
                <b-col class="text-right">
                    <b-button variant="dark" @click="clearFilters">
                        Clear Filters
                    </b-button>
                    <b-button variant="info" @click="$bvModal.show('column-customize-modal')">
                        <i class="fa fa-cog"></i> Customize Columns
                    </b-button>
                </b-col>
            </b-row>
            <hr>
            <div v-if="filteredPrescriptions && filteredPrescriptions.length" class="mb-2 print-actions glass-pane">
                <div class="d-flex align-items-center">
                    <div class="d-flex flex-fill align-items-center">
                        <div class="mr-2">
                            <b-button variant="info" @click="selectOrders">Select {{ N_ORDERS_SELECTION }}</b-button>
                        </div>
                        <div class="mr-2 d-flex" v-if="!isLoadingPrinters">
                            <b-dropdown :text="prettyPrinterName(activeLocalPrinter)" variant="outline-primary">
                                <b-dropdown-item-button :active="!activeLocalPrinter"
                                    @click="activeLocalPrinter = null">
                                    <i class="fa fa-building"></i>
                                    Store Default Printer
                                </b-dropdown-item-button>
                                <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                                    :active="p.name === activeLocalPrinter?.name" @click="activeLocalPrinter = p">
                                    <i class="fa fa-print"></i>
                                    {{ prettyPrinterName(p) }}
                                    <div class="d-flex" style="gap: 6px;">
                                        <small v-if="p.default">Local Default</small>
                                        <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                                    </div>
                                </b-dropdown-item-button>
                                <b-dropdown-item disabled v-if="!hasLocalPrinters">
                                    <i class="fa fa-info-circle" />
                                    To print to local printers, install<br>the Scriptly Client Service
                                </b-dropdown-item>
                            </b-dropdown>

                            <b-button variant="success" @click="print(false)" :disabled="!allowPrinting">
                                Print
                            </b-button>
                            <b-button v-if="allowCentralFill" variant="success" @click="print(true)"
                                :disabled="!allowPrinting">
                                Send to Central Fill
                            </b-button>
                        </div>
                    </div>
                    <div class="d-flex align-items-center">
                        <b-overlay :show="isMarkingInStock" class="d-inline-block" spinner-small>
                            <b-button v-if="showOutOfStocks" size="sm" variant="primary" :disabled="!selectedCount"
                                @click="markInStock" class="m-0">
                                Mark as In Stock
                                <template v-if="selectedCount">({{ selectedCount }})</template>
                            </b-button>
                        </b-overlay>
                        <b-button variant="success" size="sm" :disabled="!allowPrinting"
                            @click="$bvModal.show('label-reprint-modal')">
                            Print Label w/o Patient Ed
                        </b-button>
                        <b-button variant="info" size="sm" :disabled="!hasSelectedCompounds"
                            @click="openAssignLotsModal(true)">
                            Print Compound Ingredients
                        </b-button>
                        <b-button variant="danger" size="sm" :disabled="!allowPrinting" @click="removeWithoutPrinting">
                            Remove Selected w/o Printing
                        </b-button>
                        <b-button v-b-modal="'external-transfer-modal'" size="sm" variant="info" :disabled="!allowPrinting">
                            Transfer Out...
                        </b-button>
                    </div>
                </div>
            </div>

            <PrescriptionsListComponent ref="prescriptionsList" v-if="storeFilter?.id" :prescriptions="filteredPrescriptions"
                :selected="selectedRxsIn" mode="print" showPatientName showAddOns :showVoidBox="false" constrainColumnWidths
                :additionalFields="additionalFields" :fieldsKeysToRemove="fieldsToRemove" defaultSort="deliveryCode"
                :current-sort.sync="currentSort" :current-desc.sync="currentDesc" :color-codes="colorCodeTable"
                :custom-sort="customSort" :is-loading="isLoading" @selection-changed="rxSelectionChanged"
                :rx-id-link="true" :per-page="perPageSize" :hide-pager-when-not-needed="false" :total-unfiltered-rows="searchText ? basePrescriptions.length : null"
                @change-page-size="perPageSize = $event" :patient-count-fn="getPatientCount">
                <template v-if="selectedCount" #pager-fab>
                    <b-button size="sm" variant="warning" @click="selectAllForPatients">
                        Select All for {{ selectedCount === 1 ? 'Patient' : 'Patients' }}
                    </b-button>
                </template>
            </PrescriptionsListComponent>
        </card>

        <b-modal id="split-print-warning-modal" title="Prescriptions Not Selected" centered no-fade>
            <p>The following patients have prescriptions in the queue that are not selected:</p>
            <ul>
                <li v-for="patient in splitPrintWarningPatients" :key="patient.patientID">
                    <strong>{{ patient.name }}</strong> &mdash;
                    {{ patient.unselectedCount }} other Rx{{ patient.unselectedCount !== 1 ? 's' : '' }} not selected
                </li>
            </ul>
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('split-print-warning-modal')">Cancel</b-button>
                <b-button variant="warning" @click="addAllAndPrint">Add All &amp; Print</b-button>
                <b-button variant="success" @click="printAnyway">Print Anyway</b-button>
            </template>
        </b-modal>

        <b-modal id="print-queue-modal" size="xl" title="Print Stats" hide-footer @shown="printQueueModalOpen = true" @hidden="printQueueModalOpen = false">
            <PrintQueueTable v-if="printQueueModalOpen" :status="status.printQueueStatus" :s-list="status.storeList"
                @link-clicked="$bvModal.hide('print-queue-modal')" />
        </b-modal>
        <b-modal id="label-reprint-modal" title="Print Label w/o Patient Ed" size="lg" @shown="onLabelReprintModalShown">
            <b-row>
                <b-col>
                    <b-dropdown :text="activeReprintPrinter ? prettyPrinterName(activeReprintPrinter) : 'IP Address'"
                        variant="outline-primary">
                        <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                            :active="p.name === activeReprintPrinter?.name" @click="activeReprintPrinter = p">
                            <i class="fa fa-print"></i>
                            {{ prettyPrinterName(p) }}
                            <div class="d-flex" style="gap: 6px;">
                                <small v-if="p.default">Local Default</small>
                                <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                            </div>
                        </b-dropdown-item-button>
                        <b-dropdown-item disabled v-if="!hasLocalPrinters">
                            <i class="fa fa-info-circle" />
                            To print to local printers, install<br>the Scriptly Client Service
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item-button :active="!activeReprintPrinter" @click="activeReprintPrinter = null">
                            <i class="fa fa-globe"></i>
                            IP Address...
                        </b-dropdown-item-button>
                    </b-dropdown>
                    <fg-input v-if="!activeReprintPrinter" label="IP Address:" type="text" placeholder="Enter IP Address Here" v-model="printerAddress" />
                </b-col>
            </b-row>

            <!-- Lot Number Entry Section - Only shown when 5 or fewer distinct drugs -->
            <div v-if="showLotNumberEntry" class="mt-3">
                <hr>
                <h6 class="mb-3">
                    <i class="fa fa-vial"></i> Lot Number & Expiration (Optional)
                    <small class="text-muted d-block mt-1">Enter lot information to be printed on labels</small>
                </h6>
                <b-tabs pills v-model="reprintLotTabIndex" content-class="mt-3">
                    <b-tab title="Apply to all (by drug)">
                        <div v-for="drug in distinctDrugsForLotEntry" :key="`${drug.packageId}_${drug.storeId}`" class="mb-3 p-3 border rounded">
                            <div class="mb-2">
                                <strong>{{ drug.drugName }}</strong>
                                <small class="text-muted d-block">{{ drug.prescriptionCount }} prescription(s)</small>
                            </div>
                            <b-row>
                                <b-col v-if="drug.availableLots && drug.availableLots.length > 0" md="4">
                                    <label>Select from Inventory:</label>
                                    <b-form-select
                                        v-model="drug.selectedLotId"
                                        :options="buildLotOptions(drug.availableLots)"
                                        size="sm"
                                        @change="onLotSelectedForLabelReprint(drug)" />
                                </b-col>
                                <b-col :md="drug.availableLots && drug.availableLots.length > 0 ? 4 : 6">
                                    <fg-input
                                        label="Lot Number:"
                                        type="text"
                                        placeholder="Enter Lot Number"
                                        v-model="drug.lotNumber" />
                                </b-col>
                                <b-col :md="drug.availableLots && drug.availableLots.length > 0 ? 4 : 6">
                                    <label>Expiration Date:</label>
                                    <el-date-picker
                                        v-model="drug.expirationDate"
                                        placeholder="Select Expiration Date"
                                        clearable
                                        class="w-100" />
                                </b-col>
                            </b-row>
                        </div>
                    </b-tab>
                    <b-tab title="Assign individually">
                        <div v-for="(rx, idx) in individualReprintLotAssignments" :key="idx" class="mb-2 p-2 border rounded">
                            <b-row align-v="baseline">
                                <b-col md="3">
                                    <strong>{{ rx.drugName }}</strong>
                                    <small class="text-muted d-block">Rx# {{ rx.rxNumber }}-{{ rx.rfNumber }}</small>
                                </b-col>
                                <b-col md="3">
                                    <label>Select from Inventory:</label>
                                    <b-form-select v-model="rx.selectedLotId" @change="onReprintLotSelectedForIndividual(rx)"
                                        :options="buildLotOptions(rx.availableLots)" size="sm" />
                                </b-col>
                                <b-col md="3">
                                    <fg-input label="Lot Number:" type="text" placeholder="Lot #" v-model="rx.lotNumber"
                                        inputClasses="form-control-sm" />
                                </b-col>
                                <b-col md="3">
                                    <label>Expiration Date:</label>
                                    <el-date-picker v-model="rx.expirationDate" placeholder="Exp. Date"
                                        clearable class="w-100" size="small" />
                                </b-col>
                            </b-row>
                        </div>
                        <div v-if="!individualReprintLotAssignments.length" class="text-center text-muted py-4">
                            No prescriptions with valid drug information selected.
                        </div>
                    </b-tab>
                </b-tabs>
            </div>

            <template #modal-footer>
                <b-button @click="printWithoutPatEd" variant="success"
                    :disabled="!(printerAddress || activeReprintPrinter)">
                    Print
                </b-button>
            </template>
        </b-modal>
        <b-modal id="print-rx-rf-modal" title="Print by Rx/Rf" no-fade centered>
            <b-row class="mb-2">
                <b-col>
                    <fg-input label="Store" disabled :value="storeFilter" />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <fg-input label="RxNumber" type="number" v-model.number="rxToPrint.rxNumber" />
                </b-col>
                <b-col>
                    <fg-input label="RfNumber" type="number" v-model.number="rxToPrint.rfNumber" />
                </b-col>
            </b-row>
            <template #modal-footer>
                <div class="flex-fill">
                    <b-button variant="success" @click="printByRxRf"
                        :disabled="!(rxToPrint.rxNumber) || isPrintingRxRf">
                        Print
                    </b-button>
                </div>
            </template>
        </b-modal>
        <b-modal id="print-badge" title="Print Badge" no-fade>
            <b-row>
                <b-col>
                    <SearchComponent v-model="storeFilter" label="Store" search-u-r-l="/Store" :params="{active: true}" prepopulate />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-dropdown :text="activeBadgePrinter ? prettyPrinterName(activeBadgePrinter) : 'IP Address'"
                        variant="outline-primary">
                        <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                            :active="p.name === activeBadgePrinter?.name" @click="activeBadgePrinter = p">
                            <i class="fa fa-print"></i>
                            {{ prettyPrinterName(p) }}
                            <div class="d-flex" style="gap: 6px;">
                                <small v-if="p.default">Local Default</small>
                                <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                            </div>
                        </b-dropdown-item-button>
                        <b-dropdown-item disabled v-if="!hasLocalPrinters">
                            <i class="fa fa-info-circle" />
                            To print to local printers, install<br>the Scriptly Client Service
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item-button :active="!activeBadgePrinter" @click="activeBadgePrinter = null">
                            <i class="fa fa-globe"></i>
                            IP Address...
                        </b-dropdown-item-button>
                    </b-dropdown>
                    <fg-input v-if="!activeBadgePrinter" label="IP Address:" type="text" v-model="ipAddress" />
                </b-col>
            </b-row>
            <template #modal-footer>
                <b-button variant="success" @click="printBadge"
                    :disabled="!(storeFilter && storeFilter.id) || !(ipAddress || activeBadgePrinter) || isPrintingBadge">
                    Print
                </b-button>
            </template>
        </b-modal>
        <b-modal v-model="showPrintingModal" size="sm" no-fade centered body-class="text-center" hide-footer hide-header
            no-close-on-backdrop no-close-on-esc>
            <div class="printers-loading mb-2 d-flex justify-content-center align-items-center">
                <i class="far fa-file-alt" />
                <i class="far fa-file-alt" />
                <i class="far fa-file-alt" />
                <i class="fa fa-print" />
            </div>
            <strong v-if="!printProgress">Printing...</strong>
            <div v-else>
                <strong>Printing...</strong>
                <div class="mt-2">
                    <b-progress :value="printProgress.successCount + printProgress.failureCount"
                                :max="printProgress.totalCount"
                                :variant="printProgress.failureCount > 0 ? 'warning' : 'success'"
                                height="20px" />
                </div>
                <div class="mt-2">
                    <small>
                        <span class="text-success">{{ printProgress.successCount }} completed</span>
                        <span v-if="printProgress.failureCount > 0" class="text-danger ml-2">{{ printProgress.failureCount }} failed</span>
                        <span class="text-muted ml-2">{{ printProgress.remainingCount }} remaining</span>
                    </small>
                </div>
            </div>
        </b-modal>
        <!-- External Transfer Modal -->
        <ExternalTransferModal
            :prescriptions="selectedRxsOut"
            :store="storeFilter"
            @transferred="onTransferCompleted" />

        <!-- Assign Lot Numbers Modal -->
        <b-modal id="assign-lots-modal" :title="printAfterAssign ? 'Assign Lot Numbers & Print Compound Ingredients' : 'Assign Lot Numbers'" size="xl" @hidden="onAssignLotsModalHidden">
            <b-overlay :show="isLoadingAvailableLots || isAssigningLots" spinner-variant="primary">
                <div class="mb-3 d-flex align-items-center justify-content-between">
                    <small class="text-muted">{{ selectedRxsOut.length }} prescription(s) selected</small>
                </div>

                <b-tabs pills v-model="assignLotsTabIndex" content-class="mt-3">
                    <b-tab title="Apply to all (by drug)">
                        <div v-for="group in drugGroupsForLotAssignment" :key="group.groupKey" class="mb-3 p-3 border rounded">
                            <div class="mb-2">
                                <strong>{{ group.drugName }}</strong>
                                <small class="text-muted d-block">Store {{ group.storeId }} &mdash; {{ group.prescriptionCount }} prescription(s)</small>
                            </div>
                            <b-row>
                                <b-col md="4">
                                    <label>Select from Inventory:</label>
                                    <b-form-select v-model="group.selectedLotId" @change="onLotSelectedForGroup(group)"
                                        :options="buildLotOptions(group.availableLots)" size="sm" />
                                </b-col>
                                <b-col md="3">
                                    <fg-input label="Lot Number:" type="text" placeholder="Enter Lot Number"
                                        v-model="group.lotNumber" inputClasses="form-control-sm" />
                                </b-col>
                                <b-col md="3">
                                    <label>Expiration Date:</label>
                                    <el-date-picker v-model="group.expirationDate" placeholder="Select Date"
                                        clearable class="w-100" size="small" />
                                </b-col>
                                <b-col md="2">
                                    <fg-input label="Quantity:" type="number" v-model.number="group.quantity" />
                                </b-col>
                            </b-row>
                        </div>
                        <div v-if="!drugGroupsForLotAssignment.length" class="text-center text-muted py-4">
                            No prescriptions with valid drug information selected.
                        </div>
                    </b-tab>

                    <b-tab title="Assign individually">
                        <div v-for="(rx, idx) in individualLotAssignments" :key="idx" class="mb-2 p-2 border rounded">
                            <b-row align-v="baseline">
                                <b-col md="3">
                                    <strong>{{ rx.drugName }}</strong>
                                    <small class="text-muted d-block">Rx# {{ rx.rxNumber }}-{{ rx.rfNumber }}</small>
                                </b-col>
                                <b-col md="3">
                                    <label>Select from Inventory:</label>
                                    <b-form-select v-model="rx.selectedLotId" @change="onLotSelectedForIndividual(rx)"
                                        :options="buildLotOptions(rx.availableLots)" size="sm" />
                                </b-col>
                                <b-col md="2">
                                    <fg-input label="Lot Number:" type="text" placeholder="Lot #" v-model="rx.lotNumber"
                                        inputClasses="form-control-sm" />
                                </b-col>
                                <b-col md="2">
                                    <label>Expiration Date:</label>
                                    <el-date-picker v-model="rx.expirationDate" placeholder="Exp. Date"
                                        clearable class="w-100" size="small" />
                                </b-col>
                                <b-col md="2">
                                    <fg-input label="Quantity:" type="number" placeholder="Qty" v-model.number="rx.quantity"
                                        inputClasses="form-control-sm" />
                                </b-col>
                            </b-row>
                        </div>
                    </b-tab>
                </b-tabs>
            </b-overlay>

            <template #modal-footer="{ cancel }">
                <b-button variant="secondary" @click="cancel()">Cancel</b-button>
                <b-button variant="primary" @click="assignLots" :disabled="isAssigningLots">
                    <i class="fa fa-check"></i> {{ printAfterAssign ? 'Assign & Print' : 'Assign' }}
                </b-button>
            </template>
        </b-modal>

        <!-- Column Customize Modal -->
        <b-modal id="column-customize-modal" title="Customize Table Columns" size="lg" @ok="saveColumnPreferences">
            <div class="mb-3">
                <h6>Select which columns to display in the print queue table:</h6>
            </div>

            <div class="grid">
                <!-- Core table columns -->
                <h6 style="grid-column: 1 / -1;" class="mt-3 mb-2">Core Columns</h6>
                <b-form-checkbox v-model="columnVisibility.hasAddons">Add-ons</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.productNameShort">Drug</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.patientName">Patient</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.storeName">Store</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.rxId">Rx Id</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.daySupply">Day Supply</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.dispensed">Dispensed</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.qtyLeft">Quantity Left</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.fillDate">Fill Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.patientPay">Patient Pay</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.programName">Program</b-form-checkbox>

                <!-- Additional columns -->
                <h6 style="grid-column: 1 / -1;" class="mt-3 mb-2">Additional Columns</h6>
                <b-form-checkbox v-model="columnVisibility.promisedDate">Promised Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.ndc">NDC</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.rxIdWithExpirationWarning">Rx Id (with expiration)</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.transferToPharmacy">Transfer To Pharmacy</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.orderDate">Order Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.addressState">State</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.addressZip">Zip Code</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.drugsFilledDate">Filled Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.patientSex">Patient Sex</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.lotNumber">Lot Number</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.drugExpirationDate">Lot Expiration</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.orderId">Order Id</b-form-checkbox>
            </div>

            <template #modal-footer="{ ok, cancel }">
                <b-button variant="secondary" @click="cancel()">Cancel</b-button>
                <b-button variant="primary" @click="ok()">Save Preferences</b-button>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { DatePicker } from "element-ui";
import SearchComponent from '@/components/SearchComponent.vue';
import PrescriptionsListComponent from '@/components/Prescription/PrescriptionsListComponent.vue';
import { Prescription } from '@/models/Prescription';
import { Program } from '@/models/Program';
import { Store } from '@/models/Store';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import moment from "moment";
import { DeliveryCode } from "@/models/DeliveryCode";
import { OrderLabelData } from "@/models/OrderLabelData";
import PrintQueueTable from "@/components/Dashboard/PrintQueueTable.vue";
import { DashboardStatus } from "@/models/DashboardStatus";
import DispenseStatusComponent from "@/pages/Dashboard/Dashboard/DispenseStatusComponent.vue";
import ExternalTransferModal from "@/components/Transfer/ExternalTransferModal.vue";
import HelpPopup from "@/components/HelpPopup.vue";
import { debounce } from "lodash";
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import { getUserSetting, getUserSettingParsed, saveUserSetting } from "@/services/UserSettingsService";

const OldQueueDaysKey = 'stale_print_queue_old_queue_days';

@Component({
    name: "PrintQueuePage",
    components: {
        DispenseStatusComponent,
        PrintQueueTable,
        SearchComponent,
        [DatePicker.name]: DatePicker,
        PrescriptionsListComponent,
        Checkbox,
        ExternalTransferModal,
        HelpPopup,
    },
})
export default class PrintQueuePage extends Vue {
    private prescriptions: Array<OrderLabelData> = [];
    private selectedRxsIn: Array<Prescription> = [];
    private selectedRxsOut: Array<Prescription> = [];
    private splitPrintWarningPatients: Array<{ patientID: number; name: string; unselectedCount: number }> = [];
    private splitPrintCentralFill = false;
    private printerAddress: string = "";
    private rxToPrint: Prescription = new Prescription();
    private N_ORDERS_SELECTION = 10;
    private showOutOfStocks = false;
    private showConsignment = false;
    private showPrinted = false;
    private oldQueueDays: number = 0;
    private outOfStockValue = '';
    private searchText: string = "";
    private perPageSize: number = 50;
    private programFilter: Program = new Program();
    private storeFilter: Store = new Store();
    private orderDateStart: Date | null = null;
    private orderDateEnd: Date | null = null;
    private shouldOverride: boolean = false;
    protected needsOverride: boolean = false;
    private ipAddress: string = "";

    protected isLoading: boolean = false;
    protected isPrinting: boolean = false;
    protected isPrintingRxRf: boolean = false;
    protected isPrintingBadge: boolean = false;

    private localPrinters: any[] = [];
    private activeLocalPrinter: any | null = null;
    private activeBadgePrinter: any | null = null;
    private activeReprintPrinter: any | null = null;
    private isLoadingPrinters: boolean = false;
    private isMarkingInStock: boolean = false;
    private printQueueModalOpen: boolean = false;
    private printProgress: { successCount: number; failureCount: number; remainingCount: number; totalCount: number } | null = null;
    private isDispenseStatusCollapsed: boolean = false;

    // Lot number entry state
    private reprintLotMode: 'drug' | 'individual' = 'drug';
    private reprintLotTabIndex: number = 0;
    private distinctDrugsForLotEntry: Array<{
        packageId: number;
        drugSource: number;
        storeId: number;
        drugName: string;
        prescriptionCount: number;
        lotNumber: string;
        expirationDate: Date | null;
        selectedLotId: number | null;
        availableLots: Array<{ inventoryId: number; lotNumber: string; expirationDate: Date | null; quantity: number }>;
        prescriptions: Array<{ storeId: number; rxNumber: number; rfNumber: number; orderId: number }>;
    }> = [];
    private individualReprintLotAssignments: Array<{
        storeId: number;
        rxNumber: number;
        rfNumber: number;
        orderId: number;
        packageId: number;
        drugName: string;
        selectedLotId: number | null;
        lotNumber: string;
        expirationDate: Date | null;
        availableLots: Array<{ inventoryId: number; lotNumber: string; expirationDate: Date | null; quantity: number }>;
    }> = [];

    // Assign Lots modal state
    private printAfterAssign: boolean = false;
    private assignLotsMode: 'bulk' | 'individual' = 'bulk';
    private assignLotsTabIndex: number = 0;
    private isLoadingAvailableLots: boolean = false;
    private isAssigningLots: boolean = false;
    private drugGroupsForLotAssignment: Array<{
        groupKey: string;
        packageId: number;
        storeId: number;
        drugSource: number;
        drugName: string;
        prescriptionCount: number;
        selectedLotId: number | null;
        lotNumber: string;
        expirationDate: Date | null;
        quantity: number | null;
        availableLots: Array<{ inventoryId: number; lotNumber: string; expirationDate: Date | null; quantity: number; costPerUnit: number | null }>;
        prescriptions: Array<{ storeId: number; rxNumber: number; rfNumber: number; orderId: number }>;
    }> = [];
    private individualLotAssignments: Array<{
        storeId: number;
        rxNumber: number;
        rfNumber: number;
        orderId: number;
        packageId: number;
        drugName: string;
        selectedLotId: number | null;
        lotNumber: string;
        expirationDate: Date | null;
        quantity: number | null;
        availableLots: Array<{ inventoryId: number; lotNumber: string; expirationDate: Date | null; quantity: number; costPerUnit: number | null }>;
    }> = [];

    // Debounced version of loadOrders to prevent rapid API calls
    private debouncedLoadOrders = debounce(() => {
        this.loadOrders();
    }, 500);

    get fieldsToRemove(): Array<string> {
        const fieldsToHide: Array<string> = [];

        // Always remove these by default (as they were in the original code)
        fieldsToHide.push('qtyLeft', 'rxId');

        // Remove core fields based on visibility settings
        if (!this.columnVisibility.hasAddons) fieldsToHide.push('hasAddons');
        if (!this.columnVisibility.productNameShort) fieldsToHide.push('productNameShort');
        if (!this.columnVisibility.patientName) fieldsToHide.push('patientName');
        if (!this.columnVisibility.storeName) fieldsToHide.push('storeName');
        if (!this.columnVisibility.daySupply) fieldsToHide.push('daySupply');
        if (!this.columnVisibility.dispensed) fieldsToHide.push('dispensed');
        if (!this.columnVisibility.fillDate) fieldsToHide.push('fillDate');
        if (!this.columnVisibility.patientPay) fieldsToHide.push('patientPay');
        if (!this.columnVisibility.programName) fieldsToHide.push('programName');
        if (!this.columnVisibility.edit) fieldsToHide.push('edit');
        if (!this.columnVisibility.select) fieldsToHide.push('select');

        return fieldsToHide;
    }
    private priorityCodes = [DeliveryCode.Overnight, DeliveryCode.StandardOvernight];
    private currentSort: string = "";
    private currentDesc: boolean = true;

    // Column visibility preferences
    private columnVisibility: any = {
        // Core table fields from rxFields
        select: true,
        hasAddons: true,
        productNameShort: true,
        patientName: true,
        storeName: true,
        rxId: true,
        daySupply: true,
        dispensed: true,
        qtyLeft: true,
        fillDate: true,
        patientPay: true,
        programName: true,
        edit: true,
        // Additional fields (off by default to prevent table overflow)
        transferToPharmacy: false,
        orderDate: false,
        addressState: false,
        addressZip: false,
        drugsFilledDate: false,
        ndc: false,
        rxIdWithExpirationWarning: false,
        promisedDate: false,
        patientSex: false,
        lotNumber: false,
        drugExpirationDate: false,
        orderId: false,
    };

    get pageMode() {
        return this.$route.name;
    }

    get pageTitle(): string {
        if (this.isStalePrintQueue) {
            return this.$pageNames.stalePrintQueue ?? "Stale Print Queue";
        }
        return this.$pageNames.printQueue ?? "Print Queue";
    }
    get isStalePrintQueue(): boolean {
        return this.pageMode == "StalePrintQueue";
    }

    get hasPromised(): Boolean {
        return !!this.prescriptions?.find(i => i.promisedDate);
    }

    get isInvalidDateRange(): boolean {
        if (!this.orderDateStart || !this.orderDateEnd) return false;
        return this.orderDateEnd < this.orderDateStart;
    }

    get showTransferToPharmacy(): boolean {
        return this.filteredPrescriptions.some(p => p.transferToPharmacy?.length ?? 0 > 0);
    }

    get additionalFields() {
        const fields = [];

        if (this.columnVisibility.transferToPharmacy) {
            fields.push({
                index: null,
                field: { key: "transferToPharmacy", label: "Transfer", sortable: true, class: this.showTransferToPharmacy ? "col-truncate" : "d-none", },
            });
        }

        if (this.columnVisibility.orderDate) {
            fields.push({
                index: null,
                field: { key: "orderDate", label: "Order Date", sortable: true, formatter: this.dateFormatter, class: "col-narrow-date" },
            });
        }

        if (this.columnVisibility.addressState) {
            fields.push({
                index: null,
                field: { key: "addressState", label: "State", sortable: true, class: "col-compact" },
            });
        }

        if (this.columnVisibility.addressZip) {
            fields.push({
                index: null,
                field: { key: "addressZip", label: "Zip", sortable: true, class: "col-compact" },
            });
        }

        if (this.columnVisibility.drugsFilledDate) {
            fields.push({
                index: null,
                field: { key: "drugsFilledDate", label: "Filled", sortable: true, formatter: this.filledDateFormatter, class: "col-narrow" },
            });
        }

        if (this.columnVisibility.ndc) {
            fields.push({
                index: 1,
                field: { key: "ndc", label: "NDC", sortable: true, formatter: this.ndcFormatter, class: "col-narrow" },
            });
        }

        if (this.columnVisibility.rxIdWithExpirationWarning) {
            fields.push({
                index: 5,
                field: { key: "rxIdWithExpirationWarning", label: "Rx Id", class: "col-narrow" },
            });
        }

        if (this.hasPromised && this.columnVisibility.promisedDate) {
            fields.push({
                index: 0,
                field: {
                    key: "promisedDate",
                    label: "Promised",
                    sortable: true,
                    formatter: (value: any) => !value ? "" : value.toString(),
                    class: "col-narrow",
                },
            });
        }

        if (this.columnVisibility.patientSex) {
            fields.push({
                index: null,
                field: { key: "patientSex", label: "Sex", sortable: true, class: "col-compact text-center" },
            });
        }

        if (this.columnVisibility.lotNumber) {
            fields.push({
                index: null,
                field: { key: "lotNumber", label: "Lot Number", sortable: true },
            });
        }

        if (this.columnVisibility.drugExpirationDate) {
            fields.push({
                index: null,
                field: { key: "drugExpirationDate", label: "Lot Expiration", sortable: true, formatter: this.dateFormatter },
            });
        }

        if (this.columnVisibility.orderId) {
            fields.push({
                index: null,
                field: { key: "orderId", label: "Order Id", sortable: true },
            });
        }

        return fields;
    }

    get persistentOldQueueDays(): number {
        return getUserSettingParsed<number>(OldQueueDaysKey, 0);
    }

    beforeMount() {
        console.log(`%cBefore Mounting ${this.$options.name}`, 'color: greenyellow');
        this.oldQueueDays = this.persistentOldQueueDays;
        this.perPageSize = getPageSizePreference(this, 'pageSize_PrintQueue', 50);
        this.loadColumnPreferences();
        this.loadDispenseStatusCollapsedState();
    }

    @Watch("oldQueueDays")
    changedOldQueueDays(val: number, _oldVal: any) {
        saveUserSetting(OldQueueDaysKey, JSON.stringify(val));
    }

    @Watch("perPageSize")
    perPageSizeChanged(val: number) {
        savePageSizePreference(this, 'pageSize_PrintQueue', val);
    }

    @Watch("searchText")
    onSearchTextChanged() {
        (this.$refs.prescriptionsList as any)?.goToFirstPage?.();
    }

    @Watch("orderDateStart")
    orderDateStartChanged(val: Date | null) {
        this.debouncedLoadOrders();
    }

    @Watch("orderDateEnd")
    orderDateEndChanged(val: Date | null) {
        this.debouncedLoadOrders();
    }

    dateFormatter(value: Date, _key: string, _item: any) {
        if (!value) {
            return "";
        }
        return moment.utc(value).local().format("L LT");
    }

    filledDateFormatter(value: Date, _key: string, item: any) {
        if (!value || item.fillDate > item.drugsFilledDate) {
            return "";
        }
        return moment.utc(value).format("L");
    }

    ndcFormatter(value: string) {
        if (value == null) {
            return value;
        }
        return `${value.substr(0, 5)}-${value.substr(5, 4)}-${value.substr(9)}`;
    };

    private matchesSearch(prescription: OrderLabelData, searchWords: string[]): boolean {
        if (searchWords.length === 0) return true;

        const searchableText = [
            prescription.ndc11 ?? '',
            (prescription as any).ndc ?? '',
            prescription.productNameShort ?? '',
            prescription.patientFirstName ?? '',
            prescription.patientLastName ?? '',
            prescription.patientName ?? '',
            String(prescription.rxNumber ?? ''),
            prescription.rxId ?? '',
        ].join(' ').toLowerCase();

        return searchWords.every(word => searchableText.includes(word));
    }

    get basePrescriptions() {
        let results = this.prescriptions;

        // Apply Days Old filter on Stale Print Queue page
        if (this.isStalePrintQueue && this.oldQueueDays > 0) {
            results = results.filter(s => moment(s.printed) < moment().add(-1 * this.oldQueueDays, "days"));
        }

        return results;
    }

    get filteredPrescriptions() {
        let results = this.basePrescriptions;

        // Apply client-side search filter
        if (this.searchText.trim()) {
            const searchWords = this.searchText.toLowerCase().trim().split(/\s+/);
            results = results.filter(p => this.matchesSearch(p, searchWords));
        }

        return results;
    }

    async loadOrders() {
        if (!this.storeFilter.id) return;
        try {
            this.isLoading = true;
            let url = `/Print/prescriptions-pending/${this.storeFilter.id}/${(this.programFilter?.id || '')}`;
            if (this.pageMode == "StalePrintQueue") {
                url = `/Print/GetStalePrintQueue/${this.storeFilter.id}/${(this.programFilter?.id || '')}`;
            }
            if (this.showPrinted) {
                url = `/Print/recently-printed/${this.storeFilter.id}/${(this.programFilter?.id || '')}`;
            }

            //Get patient's prescriptions and format the data to be shown in the grid.
            const response = await Axios.get<OrderLabelData[]>(url, {
                params: {
                    outOfStock: this.showOutOfStocks,
                    isConsignment: this.showConsignment,
                    orderDateStart: this.orderDateStart,
                    orderDateEnd: this.orderDateEnd,
                },
            });
            let scripts: OrderLabelData[] = response.data.map((rx: any) => new OrderLabelData(rx));
            scripts = scripts.sort(this.sortDeliveryCode);
            this.prescriptions = scripts;
        } catch (err) {
            const error = err as AxiosError;
            console.warn("Error while loading queue.", { error, response: error?.response });
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isLoading = false;
        }
    }

    private toString(value: any): string {
        if (value === null || typeof value === "undefined") {
            return "";
        }
        if (value instanceof Object) {
            return Object.keys(value)
                .sort()
                .map(key => this.toString(value[key]))
                .join(" ");
        }
        return String(value);
    }

    get customSort(): (a: Prescription, b: Prescription, sortBy?: string, sortDesc?: boolean, formatter?: Function, compareOptions?: any, compareLocale?: any) => number {
        return (a: Prescription, b: Prescription, sortBy?: string, sortDesc?: boolean, _formatter?: Function, compareOptions?: any, compareLocale?: any) => {
            if (sortBy == undefined || sortBy === "deliveryCode" || sortBy === "") {
                return this.sortDeliveryCode(b, a);
            }
            let aSort = (a as any)[sortBy];
            let bSort = (b as any)[sortBy];

            // Handle patientName sorting - construct from lastName, firstName if patientName is empty
            if (sortBy === "patientName") {
                const aData = a as any;
                const bData = b as any;
                aSort = aSort || `${aData.patientLastName || ''}, ${aData.patientFirstName || ''}`;
                bSort = bSort || `${bData.patientLastName || ''}, ${bData.patientFirstName || ''}`;
            }

            // Handle null/undefined values - nulls last for ascending, nulls first for descending
            if (aSort == null && bSort == null) return 0;
            if (aSort == null) return sortDesc ? -1 : 1;
            if (bSort == null) return sortDesc ? 1 : -1;

            let result: number;
            if (typeof aSort === "boolean" && typeof bSort === "boolean") {
                result = (aSort === bSort) ? 0 : aSort ? -1 : 1;
            } else if ((typeof aSort === "number" && typeof bSort === "number") ||
                (aSort instanceof Date && bSort instanceof Date)) {
                result = aSort < bSort ? -1 : aSort > bSort ? 1 : 0;
            } else {
                result = this.toString(aSort).localeCompare(this.toString(bSort), compareLocale, compareOptions);
            }

            // Tiebreaker: when primary values are equal, sort by orderId descending (newest first)
            if (result === 0) {
                const aId = (a as any).orderId ?? 0;
                const bId = (b as any).orderId ?? 0;
                result = bId - aId;
            }

            // Apply sort direction
            return sortDesc ? -result : result;
        };
    }

    get sortDeliveryCode(): (a: Prescription, b: Prescription) => number {
        return (a: Prescription, b: Prescription) => {
            const aIsPriority = this.priorityCodes.includes(a.deliveryCode);
            const bIsPriority = this.priorityCodes.includes(b.deliveryCode);
            if (aIsPriority !== bIsPriority) return bIsPriority ? 1 : -1;

            const aIsWillCall = a.deliveryCode === DeliveryCode.WillCall;
            const bIsWillCall = b.deliveryCode === DeliveryCode.WillCall;
            if (aIsWillCall !== bIsWillCall) return bIsWillCall ? 1 : -1;

            return a.patientID - b.patientID;
        };
    }

    get patientGroupParityMap(): Map<number, boolean> {
        if (this.currentSort && this.currentSort !== 'deliveryCode') return new Map();

        const seen = new Map<number, boolean>();
        let parity = false;
        const sorted = [...this.filteredPrescriptions].sort((p, q) => {
            const pPriority = this.priorityCodes.includes(p.deliveryCode);
            const qPriority = this.priorityCodes.includes(q.deliveryCode);
            if (pPriority !== qPriority) return pPriority ? -1 : 1;
            const pWillCall = p.deliveryCode === DeliveryCode.WillCall;
            const qWillCall = q.deliveryCode === DeliveryCode.WillCall;
            if (pWillCall !== qWillCall) return pWillCall ? -1 : 1;
            return p.patientID - q.patientID;
        });
        for (const rx of sorted) {
            if (!seen.has(rx.patientID)) {
                seen.set(rx.patientID, parity);
                parity = !parity;
            }
        }
        return seen;
    }

    get patientCounts(): Map<number, { visible: number; hidden: number }> {
        const visible = new Map<number, number>();
        for (const rx of this.filteredPrescriptions) {
            visible.set(rx.patientID, (visible.get(rx.patientID) ?? 0) + 1);
        }
        const base = new Map<number, number>();
        for (const rx of this.basePrescriptions) {
            base.set(rx.patientID, (base.get(rx.patientID) ?? 0) + 1);
        }
        const result = new Map<number, { visible: number; hidden: number }>();
        for (const [patientID, visCount] of visible) {
            const baseCount = base.get(patientID) ?? visCount;
            result.set(patientID, { visible: visCount, hidden: baseCount - visCount });
        }
        return result;
    }

    getPatientCount(patientID: number) {
        return this.patientCounts.get(patientID) ?? null;
    }

    get lastStore(): any {
        return getUserSettingParsed<any>('printQueue_lastStore', null);
    }

    get status(): DashboardStatus {
        if (this.$root.$data.dashboardData) {
            return this.$root.$data.dashboardData as DashboardStatus;
        }
        return new DashboardStatus();
    }

    @Watch('storeFilter')
    lastStoreChanged(val1: any, _val2: any) {
        if (val1 == new Store()) return;
        saveUserSetting('printQueue_lastStore', JSON.stringify(val1));
    }

    private skipFilterSave = false;

    mounted() {
        this.restoreFilters();
        this.setupFromQuery(); // Query params take precedence over saved filters
        this.getLocalPrinters();
        this.loadOrders();
    }

    private restoreFilters() {
        const saved = getUserSettingParsed<any>('printQueue_filters', null);
        if (saved) {
            this.skipFilterSave = true;
            if (saved.store?.id) this.storeFilter = new Store(saved.store);
            if (saved.program?.id) this.programFilter = new Program(saved.program);
            if (typeof saved.showOutOfStocks === 'boolean') this.showOutOfStocks = saved.showOutOfStocks;
            if (typeof saved.showConsignment === 'boolean') this.showConsignment = saved.showConsignment;
            if (typeof saved.showPrinted === 'boolean') this.showPrinted = saved.showPrinted;
            this.$nextTick(() => { this.skipFilterSave = false; });
        } else {
            // Fall back to localStorage for store
            this.storeFilter = new Store(this.lastStore) ?? new Store();
        }
    }

    private persistFilters() {
        if (this.skipFilterSave) return;
        saveUserSetting('printQueue_filters', JSON.stringify({
            store: this.storeFilter?.id ? { id: this.storeFilter.id, name: this.storeFilter.name } : null,
            program: this.programFilter?.id ? { id: this.programFilter.id, name: this.programFilter.name } : null,
            showOutOfStocks: this.showOutOfStocks,
            showConsignment: this.showConsignment,
            showPrinted: this.showPrinted,
        }));
    }

    setupFromQuery() {
        const query = this.$route.query;
        if (Object.keys(query).findIndex(v => v == "storeId") != -1 && this.storeFilter?.id != Number(query.storeId)) {
            if (query.storeId) {
                this.storeFilter = new Store({ id: Number(query.storeId) } as Store);
            }
        }

        if (Object.keys(query).findIndex(v => v == "programId") != -1 && this.programFilter?.id != Number(query.programId)) {
            if (query.programId) {
                this.programFilter = new Program({ id: Number(query.programId) } as Program);
            } else {
                this.programFilter = new Program();
            }
        }

        if (Object.keys(query).findIndex(v => v == "oos") != -1 && this.showOutOfStocks != (query.oos.toString() == "true")) {
            this.showOutOfStocks = query.oos.toString() == "true";
        }

        if (Object.keys(query).findIndex(v => v == "recent") != -1 && this.showPrinted != (query.recent.toString() == "true")) {
            this.showPrinted = query.recent.toString() == "true";
        }

        if (Object.keys(query).findIndex(v => v == "consignment") != -1 && this.showConsignment != (query.consignment.toString() == "true")) {
            this.showConsignment = query.consignment.toString() == "true";
        }
    }

    @Watch('$route.query')
    queryChanged(_newValue: any, _oldValue: any) {
        this.setupFromQuery();
    }

    @Watch('showOutOfStocks')
    oosChanged(newValue: boolean, oldValue: boolean) {
        const oos = this.$route.query.oos == "true";
        if (newValue != oldValue && newValue != oos) {
            this.$router.replace({
                name: this.$route.name ?? "",
                query: { ...this.$route.query, oos: newValue.toString(), preserveScroll: "1" }
            }).catch(() => {
                // Ignore navigation duplicated errors
            });
        }
    }

    @Watch('showPrinted')
    printedChanged(newValue: boolean, oldValue: boolean) {
        const recent = this.$route.query.recent == "true";
        if (newValue != oldValue && newValue != recent) {
            this.$router.replace({
                name: this.$route.name ?? "",
                query: { ...this.$route.query, recent: newValue.toString(), preserveScroll: "1" }
            });
        }
    }

    @Watch('storeFilter')
    storeChange(newValue: Store, oldValue: Store) {
        if (newValue.id != oldValue.id && newValue.id != Number(this.$route.query.storeId)) {
            this.$router.replace({
                name: this.$route.name ?? "",
                query: { ...this.$route.query, storeId: this.storeFilter.id.toString(), preserveScroll: "1" }
            });
        }
    }

    @Watch('programFilter')
    programChange(newValue: Program, oldValue: Program) {
        if (newValue?.id != oldValue?.id && newValue?.id != Number(this.$route.query?.programId)) {
            this.$router.replace({
                name: this.$route.name ?? "",
                query: { ...this.$route.query, programId: newValue?.id?.toString(), preserveScroll: "1" }
            });
        }
    }

    @Watch('showConsignment')
    @Watch('showOutOfStocks')
    @Watch('storeFilter')
    @Watch('programFilter')
    @Watch('showPrinted')
    reloadOrders(_newValue: any, _oldValue: any) {
        if (this.skipFilterSave) return;
        this.persistFilters();
        this.loadOrders();
    }

    @Watch('pageMode') pageModeChanged() {
        this.clearFilters();
        this.prescriptions = [];
        this.selectedRxsIn = [];
        this.selectedRxsOut = [];
        this.outOfStockValue = '';
        this.rxToPrint = new Prescription();
    }

    @Watch('activeLocalPrinter')
    activeLocalPrinterChanged() {
        localStorage.setItem("active-printer", this.activeLocalPrinter?.name);
    }

    @Watch('activeBadgePrinter')
    activeBadgePrinterChanged() {
        localStorage.setItem("active-badge-printer", this.activeBadgePrinter?.name);
    }

    @Watch('activeReprintPrinter')
    activeReprintPrinterChanged() {
        localStorage.setItem("active-reprint-printer", this.activeReprintPrinter?.name);
    }

    get allowPrinting() {
        return this.selectedRxsOut?.length;
    }

    get hasSelectedCompounds() {
        return this.selectedRxsOut?.some(rx => rx.drugSource === 2); // DrugSource.CompoundProduct = 2
    }

    get allowCentralFill() {
        const centralFill = this?.$config?.VITE_APP_CENTRAL_FILL_ENABLED == "true";
        return centralFill;
    }

    selectOrders() {
        const listComponent = this.$refs.prescriptionsList as any;
        const pageItems: Prescription[] = listComponent?.getPageItems?.() ?? this.filteredPrescriptions;
        this.selectedRxsIn = pageItems.slice(0, this.N_ORDERS_SELECTION);
        this.selectedRxsOut = this.selectedRxsIn;
    }

    selectAllForPatients() {
        if (!this.selectedRxsOut?.length) return;
        const patientIDs = new Set(this.selectedRxsOut.map(rx => rx.patientID));
        const expanded = this.filteredPrescriptions.filter(rx => patientIDs.has(rx.patientID));
        this.selectedRxsIn = expanded;
        this.selectedRxsOut = expanded;
    }

    rxSelectionChanged(rows: Array<Prescription>) {
        this.selectedRxsOut = rows;
    }

    async printWithoutPatEd() {
        try {
            this.isPrinting = true;

            // Prepare batch request
            const prescriptions = [];

            // Prepare lot data map for quick lookup
            const lotDataMap = new Map<string, { lotNumber: string | null; expirationDate: Date | null }>();

            if (this.showLotNumberEntry) {
                if (this.reprintLotMode === 'individual' && this.individualReprintLotAssignments.length > 0) {
                    // Individual mode: each prescription has its own lot data
                    for (const rx of this.individualReprintLotAssignments) {
                        if (rx.lotNumber || rx.expirationDate) {
                            const key = `${rx.storeId}-${rx.rxNumber}-${rx.rfNumber}`;
                            lotDataMap.set(key, {
                                lotNumber: rx.lotNumber || null,
                                expirationDate: rx.expirationDate || null,
                            });
                        }
                    }
                } else if (this.distinctDrugsForLotEntry.length > 0) {
                    // Drug group mode: same lot for all prescriptions of same drug
                    for (const drug of this.distinctDrugsForLotEntry) {
                        if (drug.lotNumber || drug.expirationDate) {
                            for (const prescription of drug.prescriptions) {
                                const key = `${prescription.storeId}-${prescription.rxNumber}-${prescription.rfNumber}`;
                                lotDataMap.set(key, {
                                    lotNumber: drug.lotNumber || null,
                                    expirationDate: drug.expirationDate || null,
                                });
                            }
                        }
                    }
                }
            }

            // Build prescriptions array for batch request
            for (const script of this.selectedRxsOut) {
                const rxData = script as any;
                const storeId = rxData.storeID ?? rxData.StoreID;
                const rxNumber = rxData.rxNumber ?? rxData.RxNumber;
                const rfNumber = rxData.rfNumber ?? rxData.RfNumber;
                const orderId = rxData.orderId ?? rxData.OrderId;

                // Get lot data for this specific prescription
                const key = `${storeId}-${rxNumber}-${rfNumber}`;
                const prescriptionLotData = lotDataMap.get(key);

                prescriptions.push({
                    storeId,
                    rxNumber,
                    rfNumber,
                    orderId,
                    lotNumber: prescriptionLotData?.lotNumber || null,
                    expirationDate: prescriptionLotData?.expirationDate || null,
                });
            }

            const batchRequest = {
                prescriptions,
                ipAddress: this.activeReprintPrinter ? null : this.printerAddress
            };

            const response = await Axios.post<any>('/Print/SkipPatEdBatch', batchRequest);

            // Show any failures
            if (response.data.failures && response.data.failures.length > 0) {
                for (const failure of response.data.failures) {
                    this.$notification(NotificationOptions.notificationOptionsPreset(
                        `Rx ${failure.storeId}-${failure.rxNumber}-${failure.rfNumber}: ${failure.error}`,
                        NotificationOptions.NotificationTypes.warning
                    ));
                }
            }

            // If using local printer, send jobs to print with progress tracking
            if (this.activeReprintPrinter && response.data.toPrint?.length > 0) {
                await this.sendRawZplToLocalPrint(response.data.toPrint);
            } else if (response.data.successCount > 0) {
                this.$notification(NotificationOptions.notificationOptionsPreset(
                    `Printed ${response.data.successCount} label(s) successfully`,
                    NotificationOptions.NotificationTypes.success
                ));
            }

            await this.loadOrders();
            this.$bvModal.hide('label-reprint-modal');
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isPrinting = false;
        }
    }

    async print(centralFill: boolean = false) {
        const splitPatients = this.getSplitPatients();
        if (splitPatients.length > 0) {
            this.splitPrintWarningPatients = splitPatients;
            this.splitPrintCentralFill = centralFill;
            this.$bvModal.show('split-print-warning-modal');
            return;
        }
        await this.executePrint(centralFill);
    }

    getSplitPatients(): Array<{ patientID: number; name: string; unselectedCount: number }> {
        if (!this.selectedRxsOut?.length) return [];
        const selectedPatientIds = new Set(this.selectedRxsOut.map(rx => rx.patientID));
        const result: Array<{ patientID: number; name: string; unselectedCount: number }> = [];
        for (const patientId of selectedPatientIds) {
            const allVisible = this.filteredPrescriptions.filter(rx => rx.patientID === patientId);
            const selected = this.selectedRxsOut.filter(rx => rx.patientID === patientId);
            if (allVisible.length > selected.length) {
                const sample = allVisible[0];
                const name = sample.patientName?.trim() || `${sample.patientLastName}, ${sample.patientFirstName}`;
                result.push({ patientID: patientId, name, unselectedCount: allVisible.length - selected.length });
            }
        }
        return result;
    }

    addAllAndPrint() {
        this.selectAllForPatients();
        this.$bvModal.hide('split-print-warning-modal');
        this.executePrint(this.splitPrintCentralFill);
    }

    printAnyway() {
        this.$bvModal.hide('split-print-warning-modal');
        this.executePrint(this.splitPrintCentralFill);
    }

    async executePrint(centralFill: boolean = false) {
        this.isPrinting = true;
        //Get patient's prescriptions and format the data to be shown in the grid.
        const ids = {
            PrintMode: this.showPrinted ? "RecentlyPrinted" : this.pageMode,
            Labels: this.selectedRxsOut.map(rx => {
                return {
                    OrderId: rx.orderId,
                    StoreId: rx.storeID,
                    RxNumber: rx.rxNumber,
                    RfNumber: rx.rfNumber,
                    ProgramId: rx.programID,
                };
            }),
        };

        const localPrint = !!this.activeLocalPrinter;

        //Get patient's prescriptions and format the data to be shown in the grid.
        try {
            const response = await Axios.post(`/api/Print/prescriptions-pending`, ids, { params: { localPrint, centralFill } });

            if (localPrint) {
                // Show errors for prescriptions that couldn't be claimed (e.g., already being printed by another user)
                if (response.data.badLabels?.length > 0) {
                    for (const badLabel of response.data.badLabels) {
                        const rxId = badLabel.label
                            ? `${badLabel.label.storeId}-${badLabel.label.rxNumber}-${badLabel.label.rfNumber}`
                            : 'Unknown';
                        this.$notification(NotificationOptions.notificationOptionsPreset(
                            `Rx ${rxId}: ${badLabel.error}`,
                            NotificationOptions.NotificationTypes.warning
                        ));
                    }
                }

                // Only proceed with local print if there are jobs to print
                if (response.data.toPrint?.length > 0) {
                    await this.sendToLocalPrint(response.data.toPrint, true, response.data.batchId);
                } else if (response.data.badLabels?.length > 0) {
                    // All jobs failed - just reload the queue
                    await this.loadOrders();
                }
                return;
            }

            const printedLabels = response.data.printedLabels || 0;
            const sentLabels = ids.Labels.length || 0;
            // Notify the number of labels printed
            if (printedLabels) {
                this.$notification(NotificationOptions.notificationOptionsPreset(`Printed ${printedLabels} labels.`, NotificationOptions.NotificationTypes.success));
            } else if (sentLabels > printedLabels) {
                this.$notification(NotificationOptions.notificationOptionsPreset(`${sentLabels - printedLabels} labels were not printed.`, NotificationOptions.NotificationTypes.danger));
                console.error("Errors while printing", response.data);
            }
            // Reload Print Queue.
            await this.loadOrders();
        } catch (error) {
            console.warn(error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.isPrinting = false;
        }
    }

    async sendRawZplToLocalPrint(printJobs: any[]) {
        try {
            // Reset progress at the start
            this.printProgress = null;

            const printerName = this.activeReprintPrinter.name;

            // Initialize progress — count individual label copies, not prescriptions
            const totalLabelCount = printJobs.reduce((sum: number, j: any) => sum + (j.labels?.length ?? 0), 0);
            this.printProgress = {
                successCount: 0,
                failureCount: 0,
                remainingCount: totalLabelCount,
                totalCount: totalLabelCount
            };

            // Track which prescriptions have been marked as printed
            const markedAsPrinted = new Set<string>();

            // Process each job sequentially
            for (const job of printJobs) {
                const rxId = `${job.storeId}-${job.rxNumber}-${job.rfNumber}`;
                const labels: string[] = job.labels ?? [];
                const labelCount = labels.length;
                let sentCount = 0;

                // Skip jobs with no labels (server filtered them as failures)
                if (labelCount === 0) continue;

                try {
                    // Send each pre-processed label (server already handled LabelCount tokens)
                    for (let i = 0; i < labels.length; i++) {
                        const rawPrintRequest = {
                            printerName,
                            rawContent: labels[i],
                            documentName: `label-${job.storeId}-${job.rxNumber}-${job.rfNumber}-${i + 1}`,
                            copies: 1
                        };

                        // Try to use WebSocket connection via shared worker if available
                        const workerPort = (this as any).$workerPort;
                        if (workerPort) {
                            await new Promise<void>((resolve) => {
                                workerPort.postMessage(["clientServicePrintRaw", rawPrintRequest]);
                                setTimeout(resolve, 300);
                            });
                        } else {
                            // Fallback to HTTP if WebSocket not available
                            const printerEndpoint = "http://localhost:7274/print/raw";
                            await Axios.post(printerEndpoint, rawPrintRequest);
                        }
                        sentCount++;
                    }

                    // Mark as printed (only once per prescription)
                    if (!markedAsPrinted.has(rxId)) {
                        try {
                            await Axios.post("/Print/MarkAsPrinted", {
                                storeId: job.storeId,
                                rxNumber: job.rxNumber,
                                rfNumber: job.rfNumber,
                                orderId: job.orderId
                            });
                            markedAsPrinted.add(rxId);
                        } catch (err) {
                            console.error(`Failed to mark Rx ${rxId} as printed`, err);
                            this.$notification(NotificationOptions.notificationOptionsPreset(
                                `Rx ${rxId} was printed but could not be marked as complete`,
                                NotificationOptions.NotificationTypes.warning
                            ));
                        }

                        // Stamp DrugsFilledDate only after the label is actually dispatched.
                        // Best-effort — failure is logged but does not block the user.
                        try {
                            await Axios.post(`/Print/ConfirmFilled/${rxId}`);
                        } catch (confirmErr) {
                            console.warn(`Failed to confirm fill timestamp for Rx ${rxId}`, confirmErr);
                        }
                    }

                    // Update progress
                    this.printProgress.successCount += sentCount;
                    this.printProgress.remainingCount -= labelCount;
                } catch (err) {
                    console.error(`Failed to print Rx ${rxId}`, err);
                    this.$notification(NotificationOptions.notificationOptionsPreset(
                        `Failed to print Rx ${rxId}: ${(err as any).message || 'Unknown error'}`,
                        NotificationOptions.NotificationTypes.danger
                    ));

                    // Update progress — credit copies that were sent before the failure
                    this.printProgress.successCount += sentCount;
                    this.printProgress.failureCount += (labelCount - sentCount);
                    this.printProgress.remainingCount -= labelCount;
                }
            }

            // Calculate final counts before clearing progress
            const finalSuccessCount = this.printProgress.successCount;
            const finalFailureCount = this.printProgress.failureCount;

            // Clear progress when complete
            this.printProgress = null;

            // Show summary notification
            if (finalFailureCount > 0 && finalSuccessCount === 0) {
                this.$notification(NotificationOptions.notificationOptionsPreset(
                    `All ${finalFailureCount} label(s) failed to print`,
                    NotificationOptions.NotificationTypes.danger
                ));
            } else if (finalSuccessCount > 0) {
                this.$notification(NotificationOptions.notificationOptionsPreset(
                    `Printed ${finalSuccessCount} label(s) successfully`,
                    NotificationOptions.NotificationTypes.success
                ));
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            // Always clear progress when done
            this.printProgress = null;
        }
    }

    async sendToLocalPrint(jobsToPrint: any, markAsPrinted: boolean, batchId?: string) {
        try {
            // Reset progress at the start
            this.printProgress = null;

            const printerName = this.activeLocalPrinter.name;
            const apiBaseUrl = this.$http.defaults.baseURL?.replace("/api", "");

            // Get a fresh auth token to pass to the client service
            // Using ensureFresh=true to ensure token won't expire during long print jobs
            const authToken = await this.getAuthToken(true);

            // Generate unique job ID for this print request
            const jobId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            const localRequest = {
                printerName,
                jobs: jobsToPrint,
                apiBaseUrl,
                markAsPrinted,
                authToken,
                jobId,
                batchId,
            };

            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Use WebSocket connection through shared worker
                const result = await new Promise<{ success: boolean; data?: any }>((resolve) => {
                    const handleMessage = async (e: MessageEvent) => {
                        if (e.data[0] === "clientServicePrintProgress") {
                            const progress = e.data[1];
                            // Only handle progress for our job
                            if (progress.jobId === jobId) {
                                console.log(`Print progress: ${progress.successCount}/${progress.totalCount} completed, ${progress.failureCount} failed, ${progress.remainingCount} remaining`);
                                // Update the progress display
                                this.printProgress = {
                                    successCount: progress.successCount,
                                    failureCount: progress.failureCount,
                                    remainingCount: progress.remainingCount,
                                    totalCount: progress.totalCount
                                };

                                // Show notification immediately if a job failed to print
                                // Skip if it's a permission denied (handled separately below)
                                if (progress.failedJob && progress.failureMessage && !progress.permissionDeniedJob) {
                                    const jobId = `${progress.failedJob.storeId}-${progress.failedJob.rxNumber}-${progress.failedJob.rfNumber}`;
                                    this.$notification(NotificationOptions.notificationOptionsPreset(
                                        `Failed to print Rx ${jobId}: ${progress.failureMessage}`,
                                        NotificationOptions.NotificationTypes.danger
                                    ));
                                }

                                // Handle mark-as-complete failures immediately
                                if (progress.markCompleteFailedJob) {
                                    const job = progress.markCompleteFailedJob;
                                    const rxId = `${job.storeId}-${job.rxNumber}-${job.rfNumber}`;
                                    console.log(`Mark as complete failed for Rx ${rxId}, retrying with fresh token`);

                                    // Retry marking as complete with fresh token from frontend
                                    try {
                                        await Axios.post("/Print/MarkAsPrinted", {
                                            storeId: job.storeId,
                                            rxNumber: job.rxNumber,
                                            rfNumber: job.rfNumber,
                                            orderId: job.orderId
                                        });
                                        console.log(`Successfully marked Rx ${rxId} as printed via retry`);
                                    } catch (err) {
                                        console.error(`Failed to mark Rx ${rxId} as printed even after retry`, err);
                                        this.$notification(NotificationOptions.notificationOptionsPreset(
                                            `Rx ${rxId} was printed but could not be marked as complete`,
                                            NotificationOptions.NotificationTypes.warning
                                        ));
                                    }
                                }

                                // Handle permission denial - another user is printing this batch
                                if (progress.permissionDeniedJob && progress.permissionDeniedOwner) {
                                    const job = progress.permissionDeniedJob;
                                    const rxId = `${job.storeId}-${job.rxNumber}-${job.rfNumber}`;
                                    console.log(`Permission denied for Rx ${rxId}, already being printed by ${progress.permissionDeniedOwner}`);
                                    this.$notification(NotificationOptions.notificationOptionsPreset(
                                        `Rx ${rxId} is being printed by ${progress.permissionDeniedOwner}`,
                                        NotificationOptions.NotificationTypes.warning
                                    ));
                                }
                            }
                        } else if (e.data[0] === "clientServicePrintResponse") {
                            const data = e.data[1];
                            // Only handle response for our job
                            if (data.jobId === jobId) {
                                workerPort.removeEventListener("message", handleMessage);

                                // Clear progress when complete
                                this.printProgress = null;

                                // Check if we should fallback to HTTP
                                if (data.shouldFallback) {
                                    console.log("WebSocket printing failed, falling back to HTTP");
                                    resolve({ success: false });
                                    return;
                                }

                                resolve({ success: true, data });
                            }
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    workerPort.postMessage(["clientServicePrint", localRequest]);
                });

                // If WebSocket succeeded, show notifications and return
                if (result.success && result.data) {
                    const data = result.data;

                    // Note: Mark-complete failures are already handled in progress updates
                    // No need to retry them here

                    const errorCount = data.errors?.length ?? 0;
                    const printedCount = jobsToPrint.length - errorCount;

                    // Only show summary notification for successful prints
                    // Failures were already notified during progress updates
                    if (printedCount > 0) {
                        this.$notification(NotificationOptions.notificationOptionsPreset(`Printed ${printedCount} successfully`, NotificationOptions.NotificationTypes.success));
                    }

                    await this.loadOrders();
                    return;
                }

                // If we got here, we need to fallback to HTTP
                console.log("Falling back to HTTP printing");
            }

            // Fallback to HTTP if WebSocket not available or failed
            const localResponse = await Axios.post("http://localhost:7274/print", localRequest);

            const data = localResponse.data;
            const errorCount = data.errors?.length ?? 0;
            const printedCount = jobsToPrint.length - errorCount;

            if (localResponse.data.errors?.length) {
                this.$notification(NotificationOptions.notificationOptionsPreset(`${errorCount} has issues printing`, NotificationOptions.NotificationTypes.danger));
            }

            if (printedCount > 0) {
                this.$notification(NotificationOptions.notificationOptionsPreset(`Printed ${printedCount} successfully`, NotificationOptions.NotificationTypes.success));
            }

            await this.loadOrders();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            // Always clear progress when done
            this.printProgress = null;
        }
    }

    async getAuthToken(ensureFresh: boolean = false): Promise<string | null> {
        try {
            // Check auth provider type
            const authProviderType = (this as any).$authProviderType;

            if (authProviderType === "Cognito") {
                // Get Cognito token - pass ensureFresh to force refresh if token is expiring soon
                const { getToken } = await import("@/util/cognitoAuth");
                return await getToken(ensureFresh);
            } else {
                // Get MSAL/OIDC token - pass ensureFresh to force token refresh if needed
                const MicrosoftAuth = (await import("@/util/microsoftAuth")).default;
                const token = await MicrosoftAuth.fetchToken(ensureFresh);
                return token.accessToken;
            }
        } catch (err) {
            console.error("Failed to get auth token", err);
            return null;
        }
    }

    async removeWithoutPrinting() {
        try {
            const ids = {
                Labels: this.selectedRxsOut.map(rx => {
                    return {
                        OrderId: rx.orderId,
                        StoreId: rx.storeID,
                        RxNumber: rx.rxNumber,
                        RfNumber: rx.rfNumber,
                    };
                }),
            };
            await Axios.post(`/Print/RemoveFromPrintQueue`, ids);
            this.$notification(NotificationOptions.notificationOptionsPreset(`Labels Removed`, NotificationOptions.NotificationTypes.success));
            await this.loadOrders();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async printByRxRf() {
        this.rxToPrint.storeID = Number(this.storeFilter.id);
        this.rxToPrint.rfNumber = this.rxToPrint.rfNumber || 0;
        console.log('this.rxToPrint', this.rxToPrint.rxID);
        if (!this.rxToPrint.hasFullId) return;

        this.isPrintingRxRf = true;

        const localPrint = !!this.activeLocalPrinter;

        try {
            const res = await Axios.get(`/Print/${this.rxToPrint.rxID}/rx-pated`, { params: { localPrint } });

            if (localPrint) {
                // Show errors for prescriptions that couldn't be claimed (e.g., already being printed by another user)
                if (res.data.badLabels?.length > 0) {
                    for (const badLabel of res.data.badLabels) {
                        const rxId = badLabel.label
                            ? `${badLabel.label.storeId}-${badLabel.label.rxNumber}-${badLabel.label.rfNumber}`
                            : this.rxToPrint.rxID;
                        this.$notification(NotificationOptions.notificationOptionsPreset(
                            `Rx ${rxId}: ${badLabel.error}`,
                            NotificationOptions.NotificationTypes.warning
                        ));
                    }
                }

                // Only proceed with local print if there are jobs to print
                if (res.data.toPrint?.length > 0) {
                    await this.sendToLocalPrint(res.data.toPrint, false, res.data.batchId);
                }
                return;
            }

            if (res.data.printedLabels > 0 && res.data.badLabels?.length == 0) {
                const msg = `PatEd for Rx ${this.rxToPrint.rxID} was successfully printed`;
                this.$notification(NotificationOptions.notificationOptionsPreset(msg, NotificationOptions.NotificationTypes.success));
            }
        } catch (error) {
            const err = error as any;
            if ((err?.response?.data as string).includes("No Prescription found"))
                this.$notification(NotificationOptions.notificationOptionsPreset("Prescription not found", NotificationOptions.NotificationTypes.danger));
            else
                this.$notification(NotificationOptions.error(error));
        } finally {
            this.isPrintingRxRf = false;
        }
    }

    async printBadge() {
        try {
            this.isPrintingBadge = true;

            const config: AxiosRequestConfig = {
                params: {
                    storeId: this.storeFilter.id,
                    ipAddress: this.activeBadgePrinter ? null : this.ipAddress
                }
            };

            const response = await Axios.post<string>(`/Print/PrintMyBadge`, null, config);

            if (!this.activeBadgePrinter) return;

            const rawPrintRequest = {
                printerName: this.activeBadgePrinter.name,
                rawContent: response.data,
                documentName: "badge-reprint",
                copies: 1
            };

            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Use WebSocket connection through shared worker for raw printing
                await new Promise<void>((resolve, reject) => {
                    // Note: clientServicePrintRaw doesn't have a response handler in the worker,
                    // so we just fire and forget with a small delay
                    workerPort.postMessage(["clientServicePrintRaw", rawPrintRequest]);
                    setTimeout(resolve, 500);
                });
                return;
            }

            // Fallback to HTTP if WebSocket not available
            const printerEndpoint = "http://localhost:7274/print/raw";
            await Axios.post(printerEndpoint, rawPrintRequest);
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isPrintingBadge = false;
        }
    }

    async sendOutOfStock(e: any) {
        e.preventDefault();

        try {
            const res = await Axios.post<number>(`/Print/${this.outOfStockValue}/OutOfStock`, null, { params: { shouldOverride: this.shouldOverride } });
            const newStockQuantity = res.data;
            const message = `Marked out of stock. Net available: ${newStockQuantity}`;

            this.$notification(NotificationOptions.notificationOptionsPreset(message, NotificationOptions.NotificationTypes.success));
            this.needsOverride = false;
            this.shouldOverride = false;
            await this.loadOrders();
        } catch (err) {
            this.needsOverride = true;
            this.$notification(NotificationOptions.errorSaveNotificationPreset("Out Of Stock", err as AxiosError));
        } finally {
            this.outOfStockValue = '';
        }
    };

    async printCompoundIngredients() {
        try {
            // Filter selected prescriptions to only compounds
            const compoundRxs = this.selectedRxsOut.filter(rx => rx.drugSource === 2);

            if (compoundRxs.length === 0) {
                this.$notification(NotificationOptions.notificationOptionsPreset(
                    'No compound prescriptions selected',
                    NotificationOptions.NotificationTypes.warning
                ));
                return;
            }

            const localPrint = !!this.activeLocalPrinter;
            let successCount = 0;
            let errorCount = 0;

            // Print ingredient list for each compound prescription
            for (const rx of compoundRxs) {
                try {
                    const rxId = `${rx.storeID}-${rx.rxNumber}-${rx.rfNumber}`;
                    const response = await Axios.get(`/api/Print/${rxId}/compound-ingredients`, {
                        params: { localPrint }
                    });

                    if (localPrint && response.data.toPrint?.length > 0) {
                        await this.sendToLocalPrint(response.data.toPrint, false);
                    }

                    successCount++;
                } catch (error) {
                    errorCount++;
                    console.error(`Error printing compound ingredients for ${rx.rxNumber}-${rx.rfNumber}:`, error);
                }
            }

            // Show notification
            if (successCount > 0) {
                this.$notification(NotificationOptions.notificationOptionsPreset(
                    `Printed ${successCount} compound ingredient list(s)`,
                    NotificationOptions.NotificationTypes.success
                ));
            }
            if (errorCount > 0) {
                this.$notification(NotificationOptions.notificationOptionsPreset(
                    `Failed to print ${errorCount} compound ingredient list(s)`,
                    NotificationOptions.NotificationTypes.danger
                ));
            }
        } catch (error) {
            console.error('Error printing compound ingredients:', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    rxID(prescription: Prescription | null): string {
        if (!prescription) return "";
        return `${prescription.storeID}-${prescription.rxNumber}-${prescription.rfNumber}`;
    }

    // FILTERS
    search() {
        this.loadOrders();
    }

    clearFilters() {
        this.searchText = "";
        this.showConsignment = false;
        this.orderDateStart = null;
        this.orderDateEnd = null;
        this.$router.replace({
            name: this.$route.name ?? "",
            query: {
                storeId: this.storeFilter.id?.toString(),
                preserveScroll: "1",
                oos: "false",
                recent: "false",
                programId: null
            }
        });
    }

    colorCodeTable(rx: Prescription, type: any) {
        if (!rx || type !== "row") return;

        const patientClass = this.patientGroupParityMap.get(rx.patientID) ? 'patient-group-alt' : '';

        let priorityClass = '';
        if (rx.isUrgent) priorityClass = 'table-danger font-weight-bold';
        else if (this.showPrinted) priorityClass = 'table-info';
        else if (rx.deliveryCode == DeliveryCode.WillCall) priorityClass = 'table-warning';
        else if (this.priorityCodes.includes(rx.deliveryCode)) priorityClass = 'table-danger';
        else {
            const dayDiff = moment(rx.expirationDate).diff(moment(), "days");
            if (dayDiff <= 1) priorityClass = 'table-danger';
            else if (dayDiff <= 3) priorityClass = 'table-warning';
            else if (dayDiff <= 7) priorityClass = 'table-info';
        }

        return [priorityClass, patientClass].filter(Boolean).join(' ') || undefined;
    }

    async getLocalPrinters() {
        try {
            this.isLoadingPrinters = true;

            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Request printers through the shared worker
                const printers = await new Promise<any[]>((resolve) => {
                    const timeout = setTimeout(() => {
                        console.log("WebSocket printer request timed out, falling back to HTTP");
                        resolve([]);
                    }, 3000);

                    const handleMessage = (e: MessageEvent) => {
                        if (e.data[0] === "clientServicePrinters") {
                            clearTimeout(timeout);
                            workerPort.removeEventListener("message", handleMessage);
                            resolve(e.data[1] || []);
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    workerPort.postMessage(["clientServiceRefreshPrinters", null]);
                });

                if (printers.length > 0) {
                    this.localPrinters = printers;
                    this.handleDefaultPrinter();
                    return;
                }
            }

            // Fallback to HTTP if WebSocket not available or no printers received
            const response = await this.$http.get("http://localhost:7274/print/devices");
            this.localPrinters = response.data;

            this.handleDefaultPrinter();
        } catch {
            //ignore
        } finally {
            this.isLoadingPrinters = false;
        }
    }

    handleDefaultPrinter() {
        const lastPrinter = localStorage.getItem("active-printer");
        const lastBadgePrinter = localStorage.getItem("active-badge-printer");
        const lastReprintPrinter = localStorage.getItem("active-reprint-printer");

        if (lastPrinter) {
            this.activeLocalPrinter = this.localPrinters?.find(p => p.name === lastPrinter);
        }

        if (lastBadgePrinter) {
            this.activeBadgePrinter = this.localPrinters?.find(p => p.name === lastBadgePrinter);
        }

        if (lastReprintPrinter) {
            this.activeReprintPrinter = this.localPrinters?.find(p => p.name === lastReprintPrinter);
        }
    }

    get hasLocalPrinters() {
        return !!this.localPrinters.length;
    }

    get showLotNumberEntry(): boolean {
        // Get distinct drugs from selected prescriptions
        const distinctDrugs = new Map<number, any>();
        for (const rx of this.selectedRxsOut) {
            const rxData = rx as any;
            const packageId = rxData.PackageID || rxData.packageID;
            if (packageId && !distinctDrugs.has(packageId)) {
                distinctDrugs.set(packageId, {
                    packageId,
                    drugName: rxData.productNameShort || rxData.DrugName || 'Unknown Drug',
                });
            }
        }

        // Only show lot entry if there are 5 or fewer distinct drugs
        return distinctDrugs.size > 0 && distinctDrugs.size <= 5;
    }

    async prepareDistinctDrugsForLotEntry() {
        const drugMap = new Map<string, any>();
        const lotsRequestItems: Array<{ DrugSource: number; PackageId: number; StoreId: number }> = [];
        const individualAssignments: Array<any> = [];

        for (const rx of this.selectedRxsOut) {
            const rxData = rx as any;
            const packageId = rxData.PackageID || rxData.packageID;
            const drugSource = rxData.drugSource ?? rxData.DrugSource ?? 0;
            const storeId = rxData.storeID ?? rxData.StoreID;
            const drugName = rxData.productNameShort || rxData.DrugName || 'Unknown Drug';
            const rxNumber = rxData.rxNumber ?? rxData.RxNumber;
            const rfNumber = rxData.rfNumber ?? rxData.RfNumber;
            const orderId = rxData.orderId ?? rxData.OrderId;

            if (!packageId) continue;

            const groupKey = `${packageId}_${storeId}`;

            if (!drugMap.has(groupKey)) {
                drugMap.set(groupKey, {
                    packageId,
                    drugSource,
                    storeId,
                    drugName,
                    prescriptionCount: 0,
                    lotNumber: '',
                    expirationDate: null,
                    selectedLotId: null,
                    availableLots: [],
                    prescriptions: [],
                });
                lotsRequestItems.push({ DrugSource: drugSource, PackageId: packageId, StoreId: storeId });
            }

            const drug = drugMap.get(groupKey);
            drug.prescriptionCount++;
            drug.prescriptions.push({ storeId, rxNumber, rfNumber, orderId });

            individualAssignments.push({
                storeId,
                rxNumber,
                rfNumber,
                orderId,
                packageId,
                drugName,
                selectedLotId: null,
                lotNumber: '',
                expirationDate: null,
                availableLots: [],
            });
        }

        this.distinctDrugsForLotEntry = Array.from(drugMap.values());
        this.individualReprintLotAssignments = individualAssignments;

        // Fetch available inventory lots
        if (lotsRequestItems.length > 0) {
            try {
                const response = await Axios.post<Record<string, any[]>>('/Print/available-lots', {
                    items: lotsRequestItems,
                });
                const lotsByKey = response.data;
                for (const drug of this.distinctDrugsForLotEntry) {
                    const key = `${drug.packageId}_${drug.storeId}`;
                    drug.availableLots = lotsByKey[key] || [];
                }
                // Share available lots to individual assignments based on their drug
                for (const rx of this.individualReprintLotAssignments) {
                    const key = `${rx.packageId}_${rx.storeId}`;
                    rx.availableLots = lotsByKey[key] || [];
                }
            } catch (err) {
                console.warn('Error loading available lots for label reprint:', err);
            }
        }
    }

    onLotSelectedForLabelReprint(drug: any) {
        if (!drug.selectedLotId) {
            drug.lotNumber = '';
            drug.expirationDate = null;
            return;
        }
        const lot = drug.availableLots.find((l: any) => l.inventoryId === drug.selectedLotId);
        if (lot) {
            drug.lotNumber = lot.lotNumber || '';
            drug.expirationDate = lot.expirationDate ? new Date(lot.expirationDate) : null;
        }
    }

    prettyPrinterName(printer: any) {
        if (!printer && (!this.storeFilter || this.storeFilter.defaultPrinter)) return "Store Default Printer";
        if (!printer) return "No Printer Defined";
        if (!printer.serverName) return printer.name;
        return printer.name.replace(printer.serverName, "").replace(/\\/g, "");
    }

    get showPrintingModal() {
        return this.isPrinting || this.isPrintingRxRf || this.isPrintingBadge;
    }

    get selectedCount() {
        return this.selectedRxsOut?.length;
    }

    async markInStock() {
        try {
            this.isMarkingInStock = true;

            const selectedScripts = this.selectedRxsOut.map(rx => {
                return {
                    OrderId: rx.orderId,
                    StoreId: rx.storeID,
                    RxNumber: rx.rxNumber,
                    RfNumber: rx.rfNumber,
                    ProgramId: rx.programID,
                };
            });

            await Axios.post("/Print/MarkAsInStock", selectedScripts);

            await this.loadOrders();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.isMarkingInStock = false;
        }
    }


    // Transfer handling methods
    onTransferCompleted(_transferData: any) {
        this.loadOrders(); // Reload the queue after transfer
    }

    // Column customization methods
    loadColumnPreferences() {
        const savedPreferences = getUserSetting('printQueueColumnPreferences');
        if (savedPreferences) {
            try {
                const saved = typeof savedPreferences === 'string' ? JSON.parse(savedPreferences) : savedPreferences;
                // Merge saved preferences with defaults to ensure new fields have proper defaults
                this.columnVisibility = {
                    // Core table fields defaults
                    select: true,
                    hasAddons: true,
                    productNameShort: true,
                    patientName: true,
                    storeName: true,
                    rxId: true,
                    daySupply: true,
                    dispensed: true,
                    qtyLeft: true,
                    fillDate: true,
                    patientPay: true,
                    programName: true,
                    edit: true,
                    // Additional fields defaults (off by default to prevent table overflow)
                    transferToPharmacy: false,
                    orderDate: false,
                    addressState: false,
                    addressZip: false,
                    drugsFilledDate: false,
                    ndc: false,
                    rxIdWithExpirationWarning: false,
                    promisedDate: false,
                    patientSex: false,
                    lotNumber: false,
                    drugExpirationDate: false,
                    // Override with saved preferences
                    ...saved
                };
            } catch (err) {
                console.error('Failed to load column preferences:', err);
            }
        }
    }

    saveColumnPreferences() {
        try {
            saveUserSetting('printQueueColumnPreferences', JSON.stringify(this.columnVisibility));
            this.$notification(NotificationOptions.notificationOptionsPreset(
                'Column preferences saved successfully',
                NotificationOptions.NotificationTypes.success
            ));
        } catch (err) {
            console.error('Failed to save column preferences:', err);
            this.$notification(NotificationOptions.errorSaveNotificationPreset('Column Preferences', err as AxiosError));
        }
    }

    // Dispense Status collapse methods
    loadDispenseStatusCollapsedState() {
        const savedState = getUserSetting('printQueueDispenseStatusCollapsed');
        if (savedState !== null) {
            this.isDispenseStatusCollapsed = JSON.parse(savedState);
        }
    }

    toggleDispenseStatus() {
        this.isDispenseStatusCollapsed = !this.isDispenseStatusCollapsed;
        saveUserSetting('printQueueDispenseStatusCollapsed', JSON.stringify(this.isDispenseStatusCollapsed));
    }

    onLabelReprintModalShown() {
        // Reset tab state and prepare data when the modal is shown
        this.reprintLotMode = 'drug';
        this.reprintLotTabIndex = 0;
        this.prepareDistinctDrugsForLotEntry();
    }

    @Watch('reprintLotTabIndex')
    onReprintLotTabChanged(newIndex: number) {
        this.reprintLotMode = newIndex === 0 ? 'drug' : 'individual';
        // When switching to individual, carry over any drug-level lot assignments as defaults
        if (this.reprintLotMode === 'individual') {
            for (const rx of this.individualReprintLotAssignments) {
                const drug = this.distinctDrugsForLotEntry.find(d => d.packageId === rx.packageId && d.storeId === rx.storeId);
                if (drug && drug.lotNumber && !rx.lotNumber) {
                    rx.lotNumber = drug.lotNumber;
                    rx.expirationDate = drug.expirationDate;
                    rx.selectedLotId = drug.selectedLotId;
                }
            }
        }
    }

    onReprintLotSelectedForIndividual(rx: any) {
        if (!rx.selectedLotId) {
            rx.lotNumber = '';
            rx.expirationDate = null;
            return;
        }
        const lot = rx.availableLots.find((l: any) => l.inventoryId === rx.selectedLotId);
        if (lot) {
            rx.lotNumber = lot.lotNumber || '';
            rx.expirationDate = lot.expirationDate ? new Date(lot.expirationDate) : null;
        }
    }

    @Watch('assignLotsTabIndex')
    onAssignLotsTabChanged(newIndex: number) {
        this.assignLotsMode = newIndex === 0 ? 'bulk' : 'individual';
        if (this.assignLotsMode === 'individual') {
            for (const rx of this.individualLotAssignments) {
                const group = this.drugGroupsForLotAssignment.find(g => g.packageId === rx.packageId && g.storeId === rx.storeId);
                if (group && group.lotNumber && !rx.lotNumber) {
                    rx.lotNumber = group.lotNumber;
                    rx.expirationDate = group.expirationDate;
                    rx.quantity = group.quantity;
                    rx.selectedLotId = group.selectedLotId;
                }
            }
        }
    }

    onAssignLotsModalHidden() {
        this.assignLotsMode = 'bulk';
        this.assignLotsTabIndex = 0;
        this.printAfterAssign = false;
    }

    async openAssignLotsModal(printAfter: boolean = false) {
        this.printAfterAssign = printAfter;
        this.assignLotsMode = 'bulk';
        this.assignLotsTabIndex = 0;
        this.isLoadingAvailableLots = true;
        this.$bvModal.show('assign-lots-modal');

        // Build drug groups from selected prescriptions, keyed by (packageId, storeId)
        const drugMap = new Map<string, any>();
        const lotsRequestItems: Array<{ DrugSource: number; PackageId: number; StoreId: number }> = [];

        for (const rx of this.selectedRxsOut) {
            const rxData = rx as any;
            const packageId = rxData.PackageID || rxData.packageID;
            const drugSource = rxData.drugSource ?? rxData.DrugSource ?? 0;
            const drugName = rxData.productNameShort || rxData.DrugName || 'Unknown Drug';
            const storeId = rxData.storeID ?? rxData.StoreID;

            if (!packageId) continue;

            const groupKey = `${packageId}_${storeId}`;

            if (!drugMap.has(groupKey)) {
                drugMap.set(groupKey, {
                    groupKey,
                    packageId,
                    storeId,
                    drugSource,
                    drugName,
                    prescriptionCount: 0,
                    selectedLotId: null,
                    lotNumber: '',
                    expirationDate: null,
                    quantity: null,
                    availableLots: [],
                    prescriptions: [],
                });
                lotsRequestItems.push({ DrugSource: drugSource, PackageId: packageId, StoreId: storeId });
            }

            const drug = drugMap.get(groupKey);
            drug.prescriptionCount++;
            drug.prescriptions.push({
                storeId,
                rxNumber: rxData.rxNumber ?? rxData.RxNumber,
                rfNumber: rxData.rfNumber ?? rxData.RfNumber,
                orderId: rxData.orderId ?? rxData.OrderId,
            });

            // Pre-populate bulk group with existing lot data from first prescription that has it
            if (!drug.lotNumber && rxData.lotNumber) {
                drug.lotNumber = rxData.lotNumber;
            }
            if (!drug.expirationDate && rxData.drugExpirationDate) {
                drug.expirationDate = rxData.drugExpirationDate;
            }
        }

        this.drugGroupsForLotAssignment = Array.from(drugMap.values());

        // Build individual assignments
        this.individualLotAssignments = this.selectedRxsOut.map((rx: any) => ({
            storeId: rx.storeID ?? rx.StoreID,
            rxNumber: rx.rxNumber ?? rx.RxNumber,
            rfNumber: rx.rfNumber ?? rx.RfNumber,
            orderId: rx.orderId ?? rx.OrderId,
            packageId: rx.PackageID || rx.packageID || 0,
            drugName: rx.productNameShort || rx.DrugName || 'Unknown Drug',
            selectedLotId: null,
            lotNumber: rx.lotNumber || '',
            expirationDate: rx.drugExpirationDate || null,
            quantity: null,
            availableLots: [],
        }));

        // Fetch available lots from inventory
        try {
            const response = await Axios.post<Record<number, any[]>>('/Print/available-lots', {
                items: lotsRequestItems,
            });

            const lotsByKey = response.data;

            // Populate bulk groups
            for (const group of this.drugGroupsForLotAssignment) {
                group.availableLots = lotsByKey[group.groupKey] || [];
            }

            // Populate individual assignments
            for (const rx of this.individualLotAssignments) {
                const key = `${rx.packageId}_${rx.storeId}`;
                rx.availableLots = lotsByKey[key] || [];
            }
        } catch (err) {
            this.$notification(NotificationOptions.errorSaveNotificationPreset('Available Lots', err as AxiosError));
        } finally {
            this.isLoadingAvailableLots = false;
        }
    }

    buildLotOptions(availableLots: Array<{ inventoryId: number; lotNumber: string; expirationDate: Date | null; quantity: number }>) {
        const options: Array<{ value: number | null; text: string }> = [
            { value: null, text: '-- Manual Entry --' },
        ];
        for (const lot of availableLots) {
            const exp = lot.expirationDate ? new Date(lot.expirationDate).toLocaleDateString() : 'N/A';
            options.push({
                value: lot.inventoryId,
                text: `${lot.lotNumber} (Exp: ${exp}, Qty: ${lot.quantity})`,
            });
        }
        return options;
    }

    onLotSelectedForGroup(group: any) {
        if (!group.selectedLotId) return;
        const lot = group.availableLots.find((l: any) => l.inventoryId === group.selectedLotId);
        if (lot) {
            group.lotNumber = lot.lotNumber || '';
            group.expirationDate = lot.expirationDate ? new Date(lot.expirationDate) : null;
            group.quantity = lot.quantity;
        }
    }

    onLotSelectedForIndividual(rx: any) {
        if (!rx.selectedLotId) return;
        const lot = rx.availableLots.find((l: any) => l.inventoryId === rx.selectedLotId);
        if (lot) {
            rx.lotNumber = lot.lotNumber || '';
            rx.expirationDate = lot.expirationDate ? new Date(lot.expirationDate) : null;
            rx.quantity = lot.quantity;
        }
    }

    async assignLots() {
        this.isAssigningLots = true;
        const assignments: Array<{
            storeId: number; rxNumber: number; rfNumber: number; orderId: number;
            lotNumber: string; expirationDate: Date | null; quantity: number | null;
        }> = [];

        if (this.assignLotsMode === 'bulk') {
            for (const group of this.drugGroupsForLotAssignment) {
                if (!group.lotNumber && !group.expirationDate) continue;
                for (const rx of group.prescriptions) {
                    assignments.push({
                        storeId: rx.storeId,
                        rxNumber: rx.rxNumber,
                        rfNumber: rx.rfNumber,
                        orderId: rx.orderId,
                        lotNumber: group.lotNumber,
                        expirationDate: group.expirationDate,
                        quantity: group.quantity,
                    });
                }
            }
        } else {
            for (const rx of this.individualLotAssignments) {
                if (!rx.lotNumber && !rx.expirationDate) continue;
                assignments.push({
                    storeId: rx.storeId,
                    rxNumber: rx.rxNumber,
                    rfNumber: rx.rfNumber,
                    orderId: rx.orderId,
                    lotNumber: rx.lotNumber,
                    expirationDate: rx.expirationDate,
                    quantity: rx.quantity,
                });
            }
        }

        if (!assignments.length) {
            this.$notification(NotificationOptions.notificationOptionsPreset(
                'No lot information to assign',
                NotificationOptions.NotificationTypes.warning
            ));
            this.isAssigningLots = false;
            return;
        }

        try {
            const response = await Axios.post('/Print/bulk-assign-lots', { assignments });
            const result = response.data;

            if (result.failures?.length > 0) {
                // Show per-Rx failure notifications
                for (const failure of result.failures) {
                    this.$notification(NotificationOptions.notificationOptionsPreset(
                        `Rx ${failure.storeId}-${failure.rxNumber}-${failure.rfNumber}: ${failure.error}`,
                        NotificationOptions.NotificationTypes.warning
                    ));
                }

                if (result.successCount > 0) {
                    this.$notification(NotificationOptions.notificationOptionsPreset(
                        `${result.successCount} prescription(s) updated, ${result.failures.length} failed`,
                        NotificationOptions.NotificationTypes.warning
                    ));
                    this.loadOrders();
                }
                // Keep modal open so user can retry failed items
            } else {
                this.$notification(NotificationOptions.notificationOptionsPreset(
                    `${result.successCount} prescription(s) updated`,
                    NotificationOptions.NotificationTypes.success
                ));
                this.$bvModal.hide('assign-lots-modal');

                if (this.printAfterAssign) {
                    await this.printCompoundIngredients();
                }

                this.loadOrders();
            }
        } catch (err) {
            this.$notification(NotificationOptions.errorSaveNotificationPreset('Lot Assignment', err as AxiosError));
        } finally {
            this.isAssigningLots = false;
        }
    }
}

</script>

<style scoped lang="scss">

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 200px), 1fr));
}

::v-deep(.dropdown-item.active) {
    background-color: rgba(blue, .05);
    font-weight: bold;
}

@keyframes fadeIn {
    from {
        opacity: 1;
        zoom: 1;
    }
}

.printers-loading {
    font-size: 3.5em;
    gap: 10px;
    height: 100px;
}

.printers-loading>i {
    animation: fadeIn 1.5s infinite;
    opacity: 0;
    width: 60px;
    zoom: .85;
    animation-delay: 1.5s;
}

.printers-loading>i:nth-child(1) {
    animation-delay: .5s;
}

.printers-loading>i:nth-child(2) {
    animation-delay: 1s;
}

.printers-loading>i:last-child {
    animation: none;
    opacity: 1;
    font-size: 1.3em;
}

.print-actions {
    position: sticky;
    top: 0;
    z-index: 100;
}

@keyframes throb {

    0%,
    100% {
        transform: scale(1);
        opacity: 0.9;
    }

    50% {
        transform: scale(1.03);
        opacity: 1;
    }
}

.throb-badge {
    animation: throb 1.5s ease-in-out infinite;
    /* Apply the animation */
}

:deep(.el-input--small .el-input__inner) {
    height: 34px;
    border: 1px solid #C2C2C2;
}
:deep(.custom-select) {
    height: 34px;
    border: 1px solid #C2C2C2;
}

.patient-group-alt {
    box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.04);
}

</style>
